/**
 * DocumentDetailPanel Component
 * GAP-004: Desktop three-panel layout - detail panel for document preview
 *
 * Shows document details, preview, and quick actions when a document is selected.
 * Only visible on desktop (≥1200px).
 */

import { useState, type ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Badge,
  Icon,
  Spinner,
  ConfirmDialog,
  useConfirmDialog,
} from '@myvault/shared/components';
import type { Document } from '@myvault/shared';

// Extended Document type for detail panel with optional fields from API response
interface ExtendedDocument extends Document {
  original_filename?: string;
  ai_summary?: string;
  is_shared?: boolean;
  shared_count?: number;
}

interface DocumentDetailPanelProps {
  /** Selected document to display */
  document: ExtendedDocument | null;
  /** Called when the panel should close */
  onClose: () => void;
  /** Called when share is clicked */
  onShare?: (document: Document) => void;
  /** Called when delegate is clicked (GAP-032) */
  onDelegate?: (document: Document) => void;
  /** Called when delete is clicked */
  onDelete?: (document: Document) => void;
  /** Loading state */
  isLoading?: boolean;
}

export function DocumentDetailPanel({
  document,
  onClose,
  onShare,
  onDelegate,
  onDelete,
  isLoading,
}: DocumentDetailPanelProps) {
  const navigate = useNavigate();
  const { confirm, dialogProps } = useConfirmDialog();

  // Format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Format date
  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('nl-NL', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Get file type icon
  const getFileIcon = (mimeType: string): 'documentText' | 'photo' | 'document' | 'tableCells' => {
    if (mimeType.includes('pdf')) return 'documentText';
    if (mimeType.includes('image')) return 'photo';
    if (mimeType.includes('word') || mimeType.includes('document')) return 'document';
    if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return 'tableCells';
    return 'document';
  };

  // Get classification badge color
  const getClassificationColor = (classification: string): string => {
    switch (classification) {
      case 'public':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'internal':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'confidential':
        return 'bg-amber-100 text-amber-800 border-amber-200';
      case 'restricted':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  // Handle delete
  const handleDelete = () => {
    if (!document) return;
    confirm({
      title: 'Document verwijderen',
      description: `Weet je zeker dat je "${document.title}" wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt.`,
      confirmLabel: 'Verwijderen',
      variant: 'danger',
      onConfirm: async () => {
        onDelete?.(document);
      },
    });
  };

  // Handle open full view
  const handleOpenFullView = () => {
    if (document) {
      navigate(`/documents/${document.id}`);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center bg-white">
        <Spinner size="lg" />
      </div>
    );
  }

  // No document selected
  if (!document) {
    return (
      <div className="flex h-full flex-col items-center justify-center bg-mv-gray-50 p-8 text-center">
        <Icon name="document" className="h-16 w-16 text-mv-gray-300 mb-4" />
        <h3 className="text-lg font-medium text-mv-gray-700 mb-2">
          Geen document geselecteerd
        </h3>
        <p className="text-sm text-mv-gray-500 max-w-xs">
          Selecteer een document uit de lijst om de details hier te bekijken.
        </p>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col bg-white">
      {/* Header */}
      <div className="flex items-start justify-between border-b border-mv-gray-200 p-4">
        <div className="flex items-start gap-3 min-w-0">
          <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-lg bg-mv-gray-100">
            <Icon
              name={getFileIcon(document.mime_type || '')}
              className="h-6 w-6 text-mv-gray-500"
            />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="font-semibold text-mv-gray-900 truncate">{document.title}</h2>
            <p className="text-sm text-mv-gray-500 truncate">
              {document.original_filename || document.title}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="flex-shrink-0 rounded p-1 text-mv-gray-400 hover:bg-mv-gray-100 hover:text-mv-gray-600"
          aria-label="Paneel sluiten"
        >
          <Icon name="xMark" className="h-5 w-5" />
        </button>
      </div>

      {/* Preview Area */}
      <div className="flex-1 overflow-auto p-4">
        {/* Document Preview Placeholder */}
        <div className="mb-6 aspect-[4/3] rounded-lg bg-mv-gray-100 flex items-center justify-center">
          {document.mime_type?.includes('image') ? (
            <img
              src={`/api/v1/documents/${document.id}/preview`}
              alt={document.title}
              className="max-h-full max-w-full object-contain rounded-lg"
              onError={(e) => {
                // Fallback to icon if image fails to load
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
          ) : (
            <div className="text-center">
              <Icon
                name={getFileIcon(document.mime_type || '')}
                className="h-16 w-16 text-mv-gray-400 mx-auto mb-2"
              />
              <p className="text-sm text-mv-gray-500">Voorvertoning niet beschikbaar</p>
            </div>
          )}
        </div>

        {/* Metadata */}
        <div className="space-y-4">
          {/* Badges */}
          <div className="flex flex-wrap gap-2">
            {document.category && (
              <Badge variant="default" className="text-xs">
                {document.category}
              </Badge>
            )}
            <Badge className={`text-xs ${getClassificationColor(document.classification)}`}>
              {document.classification}
            </Badge>
            {document.is_shared && (
              <Badge className="text-xs bg-purple-50 text-purple-700 border-purple-200">
                <Icon name="userGroup" className="w-3 h-3 mr-1" />
                Gedeeld ({document.shared_count || 1})
              </Badge>
            )}
          </div>

          {/* Details */}
          <dl className="space-y-3 text-sm">
            <div className="flex justify-between">
              <dt className="text-mv-gray-500">Grootte</dt>
              <dd className="font-medium text-mv-gray-900">
                {formatFileSize(document.size || 0)}
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-mv-gray-500">Type</dt>
              <dd className="font-medium text-mv-gray-900">
                {document.mime_type?.split('/')[1]?.toUpperCase() || 'Onbekend'}
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-mv-gray-500">Geüpload</dt>
              <dd className="font-medium text-mv-gray-900">
                {formatDate(document.created_at)}
              </dd>
            </div>
            {document.updated_at && document.updated_at !== document.created_at && (
              <div className="flex justify-between">
                <dt className="text-mv-gray-500">Laatst gewijzigd</dt>
                <dd className="font-medium text-mv-gray-900">
                  {formatDate(document.updated_at)}
                </dd>
              </div>
            )}
          </dl>

          {/* Tags */}
          {document.tags && document.tags.length > 0 && (
            <div>
              <h4 className="text-xs font-medium text-mv-gray-500 uppercase tracking-wide mb-2">
                Tags
              </h4>
              <div className="flex flex-wrap gap-1">
                {document.tags.map((tag) => (
                  <Badge key={tag} variant="secondary" className="text-xs">
                    {tag}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* AI Summary (if available) */}
          {document.ai_summary && (
            <div className="rounded-lg bg-mv-blue-50 p-3">
              <h4 className="text-xs font-medium text-mv-blue-700 uppercase tracking-wide mb-1">
                AI Samenvatting
              </h4>
              <p className="text-sm text-mv-blue-900">{document.ai_summary}</p>
            </div>
          )}
        </div>
      </div>

      {/* Actions Footer */}
      <div className="border-t border-mv-gray-200 p-4 space-y-3">
        {/* Primary Action */}
        <Button
          variant="primary"
          className="w-full"
          onClick={handleOpenFullView}
        >
          <Icon name="eye" className="w-4 h-4 mr-2" />
          Openen
        </Button>

        {/* Secondary Actions */}
        <div className="grid grid-cols-4 gap-2">
          <Button
            variant="outline"
            size="sm"
            className="flex-1"
            onClick={() => window.open(`/api/v1/documents/${document.id}/download`, '_blank')}
            title="Downloaden"
          >
            <Icon name="arrowDownTray" className="w-4 h-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="flex-1"
            onClick={() => onShare?.(document)}
            title="Delen"
          >
            <Icon name="share" className="w-4 h-4" />
          </Button>
          {/* GAP-032: Delegate access button */}
          <Button
            variant="outline"
            size="sm"
            className="flex-1 text-purple-600 hover:bg-purple-50"
            onClick={() => onDelegate?.(document)}
            title="Toegang delegeren"
          >
            <Icon name="userPlus" className="w-4 h-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="flex-1 text-red-600 hover:bg-red-50"
            onClick={handleDelete}
            title="Verwijderen"
          >
            <Icon name="trash" className="w-4 h-4" />
          </Button>
        </div>

        {/* Keyboard hint */}
        <p className="text-xs text-mv-gray-400 text-center">
          Druk op <kbd className="px-1 py-0.5 bg-mv-gray-100 rounded text-mv-gray-600">Esc</kbd> om te sluiten
        </p>
      </div>

      {/* Confirm Dialog */}
      <ConfirmDialog {...dialogProps} />
    </div>
  );
}

export default DocumentDetailPanel;
